import { createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Trash2, X } from "@fleet/icons";
import type { Attachment } from "@journal/powersync";
import { resolveStorageUrl } from "@/lib/api";
import { useI18n } from "@/providers/i18n-provider";

interface PhotoGridProps {
  photos: () => Attachment[];
  /** If passed, shows the per-photo delete button. */
  onDelete?: (photoId: string) => void;
  /** URL-addressed lightbox: id of the zoomed photo (`?photo=<id>`). */
  openPhotoId?: () => string | null;
  /** Notifies lightbox open/close so the URL can reflect it. */
  onOpenPhotoChange?: (photoId: string | null) => void;
}

/**
 * Photo grid for an entry + simple lightbox (no dependencies).
 *
 * The lightbox is URL-addressable: if the parent passes `openPhotoId`/`onOpenPhotoChange`,
 * the zoomed photo lives in the URL (`?photo=<id>`) and is shareable/reloadable.
 * Without those props it falls back to an internal signal (uncontrolled use).
 */
export function PhotoGrid(props: PhotoGridProps) {
  const { t } = useI18n();
  const [localLightboxId, setLocalLightboxId] = createSignal<string | null>(null);

  const isControlled = () => props.openPhotoId !== undefined;
  const openId = () => (isControlled() ? (props.openPhotoId?.() ?? null) : localLightboxId());
  const setOpenId = (id: string | null) => {
    if (isControlled()) {
      props.onOpenPhotoChange?.(id);
    } else {
      setLocalLightboxId(id);
    }
  };

  // Resolves the zoomed photo from the id (controlled or local). If the URL id
  // matches no photo of this entry, nothing opens (no-op), so a stale `?photo=`
  // degrades gracefully.
  const lightbox = createMemo<Attachment | null>(() => {
    const id = openId();
    if (id === null) {
      return null;
    }
    return props.photos().find((p) => p.id === id) ?? null;
  });

  return (
    <Show when={props.photos().length > 0}>
      <div class="flex flex-wrap gap-2" data-testid="photo-grid">
        <For each={props.photos()}>
          {(photo) => (
            <div class="group relative">
              <button
                type="button"
                class="block h-20 w-20 overflow-hidden rounded-lg border border-border/40 bg-muted/40"
                onClick={() => setOpenId(photo.id)}
                aria-label={photo.filename ?? t(msg`Photo`)}
              >
                <img
                  src={resolveStorageUrl(photo.url) ?? ""}
                  alt={photo.filename ?? ""}
                  class="h-full w-full object-cover transition-transform duration-150 group-hover:scale-105"
                  loading="lazy"
                  data-testid="entry-photo"
                />
              </button>
              <Show when={props.onDelete}>
                <button
                  type="button"
                  class="absolute -right-1.5 -top-1.5 hidden h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow group-hover:flex"
                  aria-label={t(msg`Delete photo`)}
                  onClick={() => props.onDelete?.(photo.id)}
                >
                  <Trash2 class="h-3 w-3" />
                </button>
              </Show>
            </div>
          )}
        </For>
      </div>

      <Show when={lightbox()}>
        {(photo) => (
          <div
            class="fixed inset-0 z-[400] flex items-center justify-center bg-black/80 p-6"
            onPointerDown={(e) => {
              if (e.target === e.currentTarget) {
                setOpenId(null);
              }
            }}
          >
            <img
              src={resolveStorageUrl(photo().url) ?? ""}
              alt={photo().filename ?? ""}
              class="max-h-full max-w-full rounded-xl object-contain shadow-2xl"
            />
            <button
              type="button"
              class="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur hover:bg-white/20"
              aria-label={t(msg`Close`)}
              onClick={() => setOpenId(null)}
            >
              <X class="h-5 w-5" />
            </button>
          </div>
        )}
      </Show>
    </Show>
  );
}
